import { useMemo, useState, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCampaignFromCache } from '@/hooks/queries/useCampaignFromCache';
import { useAppConfigQuery } from '@/hooks/queries/useOptimizationQuery';
import { useActiveAccountsCheck } from '@/hooks/useActiveAccountsCheck';
import { useRDACache, type RDACacheEntry } from '@/hooks/useRDACache';
import { useDemandGenCache, type DemandGenCacheEntry } from '@/hooks/useDemandGenCache';
import { useCampaignStore } from '@/store/campaignStore';
import { extractLandingPageUrls } from '@/utils/landingPages';
import { supportsAdGroups } from '@/types/adGroup';
import { debug } from '@/utils/debug';
import type { Campaign, LandingPage } from '@/types/campaign';
import { isMetaCampaign } from '@/types/campaign';

export interface StructureSummary {
  adGroupCount: number;
  totalAds: number;
  totalAssets: number;
  assetGroupCount: number;
}

export interface LandingPageData {
  uniqueUrls: string[];
  urlDetails: LandingPage[];
}

export interface UseCampaignDataReturn {
  // Raw data
  campaign: Campaign | undefined;
  appConfig: ReturnType<typeof useAppConfigQuery>['data'];
  rdaCache: Record<string, RDACacheEntry>;
  isLoadingRDAs: boolean;
  demandGenCache: Record<string, DemandGenCacheEntry>;
  isLoadingDemandGenAds: boolean;

  // Filtered/derived data
  displayCampaign: Campaign | undefined;
  landingPageData: LandingPageData;
  structureSummary: StructureSummary | null;
  hasFullStructure: boolean;

  // Loading/error states
  isLoading: boolean;
  isCheckingAccounts: boolean;
  error: Error | null;

  // Removed entities management
  removedEntities: Set<string>;
  handleEntityRemoved: (entityId: string) => void;
  resetRemovedEntities: () => void;

  // Query invalidation
  invalidateCampaign: () => Promise<void>;
}

/**
 * Hook that encapsulates all campaign data fetching and derived data calculations.
 */
export function useCampaignData(campaignId: string): UseCampaignDataReturn {
  const queryClient = useQueryClient();

  // Check if user has active accounts BEFORE loading campaign
  const { isCheckingAccounts } = useActiveAccountsCheck({
    componentName: 'Campaign',
    verifyViaAPI: false
  });

  // Data fetching
  const { data: campaign, isLoading: isLoadingCampaign, error } = useCampaignFromCache(campaignId);
  const { data: appConfig } = useAppConfigQuery();
  // RDA / Demand Gen caches are Google-only entity types. Skip the
  // queries entirely when the campaign came from Meta so we don't fire
  // pointless `/assets/rdas` / `/assets/demand-gen` requests that
  // return empty.
  const isMeta = isMetaCampaign(campaign ?? { platform_source: undefined });
  const { rdaCache, isLoading: isLoadingRDAs } = useRDACache(isMeta ? undefined : campaign);
  const { demandGenCache, isLoading: isLoadingDemandGenAds } = useDemandGenCache(isMeta ? undefined : campaign);

  // Config refresh handling
  const refreshConfigOnNav = useCampaignStore((state) => state.refreshConfigOnNav);
  const setRefreshConfigOnNav = useCampaignStore((state) => state.setRefreshConfigOnNav);

  // Track removed entities for optimistic updates
  const [removedEntities, setRemovedEntities] = useState<Set<string>>(new Set());

  // Handle config refresh on navigation if landing page permissions were updated
  useEffect(() => {
    const handleRefreshOnNav = async () => {
      if (refreshConfigOnNav) {
        debug.log('useCampaignData: Refresh flag detected, refreshing configuration...');

        await queryClient.invalidateQueries({ queryKey: ['app-config'] });
        await queryClient.invalidateQueries({ queryKey: ['campaign-from-cache'] });
        await queryClient.invalidateQueries({ queryKey: ['campaigns'] });

        debug.log('useCampaignData: Configuration refreshed');
        setRefreshConfigOnNav(false);
      }
    };

    handleRefreshOnNav();
  }, [campaignId, refreshConfigOnNav, queryClient, setRefreshConfigOnNav]);

  // Log campaign data when it changes
  useEffect(() => {
    if (campaign) {
      debug.log('useCampaignData: Data loaded from cache/API', {
        has_ad_groups: !!campaign.ads_group_list,
        ad_groups_count: campaign.ads_group_list?.length,
        has_asset_groups: !!campaign.asset_group_list,
        asset_groups_count: campaign.asset_group_list?.length,
        has_account_id: !!campaign.google_ads_account_id,
        campaign_type: campaign.campaign_type,
      });
    }
  }, [campaign]);

  // Filter out removed entities and REMOVED status entities
  const displayCampaign = useMemo(() => {
    if (!campaign) return campaign;

    const filtered = { ...campaign };

    // Filter ad groups and their ads
    if (filtered.ads_group_list) {
      filtered.ads_group_list = filtered.ads_group_list
        .filter(ag => {
          const adGroupId = ag.ad_group_id || ag.ads_group_id || '';
          return !removedEntities.has(adGroupId) && ag.status !== 'REMOVED';
        })
        .map(ag => {
          if (ag.ads_list || ag.ads) {
            const adsList = ag.ads_list || ag.ads || [];
            const filteredAds = adsList.filter(ad => {
              const adId = ad.ad_id || '';
              return !removedEntities.has(adId) && ad.status !== 'REMOVED';
            });
            return { ...ag, ads_list: filteredAds, ads: filteredAds };
          }
          return ag;
        });
    }

    // Filter asset groups
    if (filtered.asset_group_list) {
      filtered.asset_group_list = filtered.asset_group_list.filter(ag => {
        const assetGroupId = ag.asset_group_id || '';
        const status = ag.status || ag.asset_group_status || '';
        return !removedEntities.has(assetGroupId) && status !== 'REMOVED';
      });
    }

    // Filter Meta ad sets and their nested ads — parity with the Google
    // ads_group_list/ads filter above. Without this, an archive
    // confirmed in the Campaign Structure card (which subscribes to
    // `useOptimisticEntityUpdates` directly and hides terminal-status
    // entries on its own) still appears in the Manage Campaign card,
    // which receives `displayCampaign` as a prop and never sees the
    // optimistic removal. Mirrors the Google logic: drop entries in
    // `removedEntities` OR with a terminal status. `META_HIDDEN_STATUSES`
    // (defined in `MetaCampaignElementsList`) is `['ARCHIVED', 'DELETED']`
    // — replicated here as a local constant to avoid the cross-import
    // (`MetaCampaignElementsList` doesn't export it).
    const META_TERMINAL_STATUSES = new Set(['ARCHIVED', 'DELETED']);
    if (filtered.ad_sets) {
      filtered.ad_sets = filtered.ad_sets
        .filter(as => {
          const adSetId = as.ad_set_id || '';
          const status = (as.status ?? '').toUpperCase();
          return !removedEntities.has(adSetId) && !META_TERMINAL_STATUSES.has(status);
        })
        .map(as => {
          if (!as.ads) return as;
          const filteredAds = as.ads.filter(ad => {
            const adId = ad.ad_id || '';
            const status = (ad.status ?? '').toUpperCase();
            return !removedEntities.has(adId) && !META_TERMINAL_STATUSES.has(status);
          });
          return { ...as, ads: filteredAds };
        });
    }

    return filtered;
  }, [campaign, removedEntities]);

  // Extract landing pages from campaign structure
  const landingPageData = useMemo((): LandingPageData => {
    if (!displayCampaign) return { uniqueUrls: [], urlDetails: [] };
    const extracted = extractLandingPageUrls(displayCampaign);
    debug.log('useCampaignData: Extracted landing pages from structure', {
      landing_pages_count: extracted.urlDetails.length,
      authorized_count: extracted.urlDetails.filter(p => p.authorized).length,
    });
    return extracted;
  }, [displayCampaign]);

  // Calculate structure summary
  const structureSummary = useMemo((): StructureSummary | null => {
    if (!displayCampaign) return null;

    // Meta campaigns: count ad sets and the ads nested under them.
    // `adGroupCount` and `totalAds` are reused (they're platform-neutral
    // labels at the data layer; the UI relabels for Meta).
    if (isMetaCampaign(displayCampaign)) {
      const adSets = displayCampaign.ad_sets ?? [];
      const adGroupCount = adSets.length;
      const totalAds = adSets.reduce((sum, as) => sum + (as.ads?.length ?? 0), 0);
      return { adGroupCount, totalAds, totalAssets: 0, assetGroupCount: 0 };
    }

    const isPMax = displayCampaign.campaign_type?.includes('PERFORMANCE_MAX');

    // Performance Max campaigns - show asset groups and assets
    if (isPMax || (displayCampaign.asset_group_list && displayCampaign.asset_group_list.length > 0)) {
      const assetGroupCount = displayCampaign.asset_group_list?.length || 0;
      const totalAssets = displayCampaign.asset_group_list?.reduce((sum, ag) =>
        sum + (ag.asset_list?.length || 0), 0
      ) || 0;

      return { adGroupCount: 0, totalAds: 0, totalAssets, assetGroupCount };
    }

    // Campaigns with ad groups (Search, Display, Shopping, AI MAX)
    if (displayCampaign.ads_group_list && displayCampaign.ads_group_list.length > 0) {
      const adGroupCount = displayCampaign.ads_group_list.length;
      const totalAds = displayCampaign.ads_group_list.reduce((sum, ag) => {
        const adsFromAds = ag.ads?.length || 0;
        const adsFromAdsList = ag.ads_list?.length || 0;
        return sum + Math.max(adsFromAds, adsFromAdsList);
      }, 0);
      const totalAssets = displayCampaign.ads_group_list.reduce((sum, ag) => {
        const adsList = ag.ads || ag.ads_list || [];
        return sum + adsList.reduce((adSum, ad) => {
          const headlineCount = ad.headlines?.length || 0;
          const descriptionCount = ad.descriptions?.length || 0;
          return adSum + headlineCount + descriptionCount;
        }, 0);
      }, 0);

      return { adGroupCount, totalAds, totalAssets, assetGroupCount: 0 };
    }

    // Campaigns with type but no structure loaded yet
    if (displayCampaign.campaign_type && supportsAdGroups(displayCampaign.campaign_type)) {
      return { adGroupCount: 0, totalAds: 0, totalAssets: 0, assetGroupCount: 0 };
    }

    return null;
  }, [displayCampaign]);

  // Check if we have the full campaign structure
  const hasFullStructure = Boolean(
    (displayCampaign?.ads_group_list && displayCampaign.ads_group_list.length > 0) ||
    (displayCampaign?.asset_group_list && displayCampaign.asset_group_list.length > 0) ||
    (displayCampaign?.ad_sets && displayCampaign.ad_sets.length > 0)
  );

  // Entity removal handler
  const handleEntityRemoved = useCallback((entityId: string) => {
    setRemovedEntities(prev => {
      const next = new Set(prev);
      next.add(entityId);
      return next;
    });
  }, []);

  // Reset removed entities (used on discard)
  const resetRemovedEntities = useCallback(() => {
    setRemovedEntities(new Set());
  }, []);

  // Invalidate campaign queries
  const invalidateCampaign = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['campaign-from-cache', campaignId] });
  }, [queryClient, campaignId]);

  const isLoading = isCheckingAccounts || isLoadingCampaign;

  return {
    campaign,
    appConfig,
    rdaCache,
    isLoadingRDAs,
    demandGenCache,
    isLoadingDemandGenAds,
    displayCampaign,
    landingPageData,
    structureSummary,
    hasFullStructure,
    isLoading,
    isCheckingAccounts,
    error: error as Error | null,
    removedEntities,
    handleEntityRemoved,
    resetRemovedEntities,
    invalidateCampaign,
  };
}
